import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminChatBubble from './AdminChatBubble';
import { 
  LayoutDashboard, Mail, Users, Settings, LogOut, 
  ChevronLeft, ChevronRight, ChevronDown, ExternalLink, Menu, X,
  FileText, BarChart3, Shield, Mic, Newspaper, Send, DollarSign, 
  Calendar, Flag, Home, Eye, Globe, Layers, Megaphone, Cog, Link2
} from 'lucide-react';
import { Button } from './ui/button';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Navigation structure with groups
  const navGroups = [
    {
      id: 'overview',
      name: 'Overview',
      icon: BarChart3,
      type: 'single',
      path: '/admin/analytics',
      description: 'Dashboard & Statistics'
    },
    {
      id: 'pages',
      name: 'Page Management',
      icon: Layers,
      type: 'group',
      items: [
        { name: 'Homepage', icon: Home, path: '/admin/homepage', description: 'Hero, sections & CTA' },
        { name: 'Program', icon: Calendar, path: '/admin/program', description: 'Schedule & agenda' },
        { name: 'Speakers', icon: Mic, path: '/admin/speakers', description: 'Speaker profiles' },
        { name: 'Sponsorship', icon: DollarSign, path: '/admin/sponsorship', description: 'Tiers & benefits' },
        { name: 'Golf Outing', icon: Flag, path: '/admin/golf', description: 'Event details' }
      ]
    },
    {
      id: 'marketing',
      name: 'Email & Marketing',
      icon: Megaphone,
      type: 'group',
      items: [
        { name: 'Email Campaigns', icon: Mail, path: '/admin/email', description: 'Create & send' },
        { name: 'Contact Lists', icon: Users, path: '/admin/email/lists', description: 'Reusable recipients' },
        { name: 'Lead Database', icon: LayoutDashboard, path: '/admin/dashboard', description: 'Form submissions' },
        { name: 'Suppression List', icon: Shield, path: '/admin/suppression', description: 'Unsubscribes' },
        { name: 'Speaker Invites', icon: Send, path: '/admin/speaker-invite', description: 'Private links' },
        { name: 'Link Hub', icon: Link2, path: '/admin/link-hub', description: 'Linktree-style pages' }
      ]
    },
    {
      id: 'settings',
      name: 'Site Controls',
      icon: Cog,
      type: 'group',
      items: [
        { name: 'Global Content', icon: Globe, path: '/admin/global-content', description: 'Site-wide text' },
        { name: 'Visibility', icon: Eye, path: '/admin/visibility', description: 'Show/hide sections' },
        { name: 'Coming Soon', icon: Globe, path: '/admin/coming-soon', description: 'Maintenance mode' },
        { name: 'Settings', icon: Settings, path: '/admin/settings', description: 'Admin preferences' }
      ]
    }
  ];

  // Determine which groups should be expanded based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const newExpanded = {};
    
    navGroups.forEach(group => {
      if (group.type === 'group') {
        const isActiveInGroup = group.items.some(item => currentPath.startsWith(item.path));
        if (isActiveInGroup) {
          newExpanded[group.id] = true;
        }
      }
    });
    
    setExpandedGroups(prev => ({ ...prev, ...newExpanded }));
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const isActive = (path) => location.pathname.startsWith(path);

  const NavItem = ({ item, collapsed, nested = false }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    
    return (
      <button
        onClick={() => {
          navigate(item.path);
          setMobileMenuOpen(false);
        }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg overflow-hidden group relative tracking-normal ${
          nested ? 'pl-10' : ''
        } ${
          active 
            ? 'bg-yellow-500 text-white shadow-lg' 
            : 'text-gray-300 hover:bg-white/10 hover:text-white'
        }`}
        style={{ transition: 'background-color 200ms ease, color 200ms ease, box-shadow 200ms ease' }}
        title={collapsed ? item.name : undefined}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-white' : ''}`} />
        {!collapsed && (
          <span className="font-medium tracking-normal text-sm">{item.name}</span>
        )}
      </button>
    );
  };

  const NavGroup = ({ group, collapsed }) => {
    const Icon = group.icon;
    const isExpanded = expandedGroups[group.id];
    const hasActiveChild = group.items?.some(item => isActive(item.path));
    
    if (group.type === 'single') {
      return <NavItem item={group} collapsed={collapsed} />;
    }

    return (
      <div className="space-y-1">
        {/* Group Header */}
        <button
          onClick={() => !collapsed && toggleGroup(group.id)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg overflow-hidden group relative tracking-normal ${
            hasActiveChild && collapsed
              ? 'bg-white/10 text-white'
              : 'text-gray-300 hover:bg-white/10 hover:text-white'
          }`}
          style={{ transition: 'background-color 200ms ease, color 200ms ease' }}
          title={collapsed ? group.name : undefined}
        >
          <Icon className={`w-5 h-5 flex-shrink-0 ${hasActiveChild ? 'text-yellow-400' : ''}`} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left font-semibold text-sm tracking-wide uppercase">
                {group.name}
              </span>
              <ChevronDown 
                className={`w-4 h-4 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`} 
              />
            </>
          )}
          {hasActiveChild && collapsed && (
            <span className="absolute right-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-yellow-400 rounded-full" />
          )}
        </button>

        {/* Group Items - Collapsible */}
        {!collapsed && (
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="space-y-1 pt-1">
              {group.items.map((item) => (
                <NavItem key={item.path} item={item} collapsed={collapsed} nested />
              ))}
            </div>
          </div>
        )}

        {/* Collapsed: Show popup menu on hover */}
        {collapsed && (
          <div className="hidden group-hover:block absolute left-full top-0 ml-2 bg-[#003366] rounded-lg shadow-xl py-2 min-w-48 z-50">
            <div className="px-3 py-2 border-b border-white/10 mb-1">
              <span className="text-xs font-semibold text-yellow-400 uppercase">{group.name}</span>
            </div>
            {group.items.map((item) => {
              const ItemIcon = item.icon;
              const itemActive = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2 ${
                    itemActive 
                      ? 'bg-yellow-500 text-white' 
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <ItemIcon className="w-4 h-4" />
                  <span className="text-sm">{item.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Mobile Navigation rendering
  const MobileNavGroup = ({ group }) => {
    const Icon = group.icon;
    const isExpanded = expandedGroups[group.id];
    const hasActiveChild = group.items?.some(item => isActive(item.path));

    if (group.type === 'single') {
      return <NavItem item={group} collapsed={false} />;
    }

    return (
      <div className="space-y-1">
        <button
          onClick={() => toggleGroup(group.id)}
          className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg ${
            hasActiveChild
              ? 'bg-white/10 text-white'
              : 'text-gray-300 hover:bg-white/10 hover:text-white'
          }`}
        >
          <Icon className={`w-5 h-5 ${hasActiveChild ? 'text-yellow-400' : ''}`} />
          <span className="flex-1 text-left font-semibold text-sm uppercase">{group.name}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        <div className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-96' : 'max-h-0'
        }`}>
          <div className="space-y-1 pl-4">
            {group.items.map((item) => (
              <NavItem key={item.path} item={item} collapsed={false} nested />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex flex-col bg-[#00457f] text-white transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo Section */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img 
              src="/assets/images/wnc-logo.png" 
              alt="WNC" 
              className="w-10 h-10 object-contain"
            />
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-lg leading-tight">WNC Admin</h1>
                <p className="text-xs text-gray-300">Control Panel</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navGroups.map((group) => (
            <NavGroup key={group.id} group={group} collapsed={!sidebarOpen} />
          ))}
        </nav>

        {/* Bottom Section */}
        <div className="p-4 border-t border-white/10 space-y-2">
          {/* Preview Site (with drafts) */}
          <button
            onClick={() => window.open('/?preview=1', '_blank')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-amber-300 hover:bg-amber-500/15 hover:text-amber-200 transition-colors ${
              !sidebarOpen ? 'justify-center' : ''
            }`}
            title={!sidebarOpen ? 'Preview Site' : undefined}
            data-testid="preview-site-btn"
          >
            <Eye className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm">Preview Site</span>}
          </button>

          {/* Return to Website */}
          <button
            onClick={() => window.open('/', '_blank')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-colors ${
              !sidebarOpen ? 'justify-center' : ''
            }`}
            title={!sidebarOpen ? 'View Website' : undefined}
          >
            <ExternalLink className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm">View Website</span>}
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors ${
              !sidebarOpen ? 'justify-center' : ''
            }`}
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm">Logout</span>}
          </button>

          {/* Collapse Toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
          >
            {sidebarOpen ? (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-xs">Collapse</span>
              </>
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#00457f] text-white shadow-lg">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <img 
              src="/assets/images/wnc-logo.png" 
              alt="WNC" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold">WNC Admin</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#00457f] border-t border-white/10 shadow-lg max-h-[80vh] overflow-y-auto">
            <nav className="p-4 space-y-2">
              {navGroups.map((group) => (
                <MobileNavGroup key={group.id} group={group} />
              ))}
              
              <div className="pt-4 border-t border-white/10 space-y-2">
                <button
                  onClick={() => {
                    window.open('/', '_blank');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span>View Website</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:overflow-auto">
        <div className="lg:hidden h-14"></div> {/* Spacer for mobile header */}
        {children}
      </main>
      <AdminChatBubble />
    </div>
  );
};

export default AdminLayout;
